import React, { useEffect, useCallback, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  Container,
  Flex,
  FlexItem,
  H1,
  H2,
  HeaderInfo,
  HeaderInfoElement,
  styled,
  Tag,
  Text,
  Tooltip,
} from '@erecruit/ui-kit';

import { Intl } from '@erecruit/core';
import { Resources, AppPermissions } from '@erecruit/types';
import { TabLayout } from '@erecruit/modules';

import { MatchHeaderProps, MatchRecordProps } from './types';

import { RootState } from '../../redux/reducers';
import { InsightSpinner } from '../../App/components/InsightPortal';

import { fetchMatchInfo, matchAction, selectors } from './state/reducer';
import {
  Credentials,
  RejectModal,
  OfferInterviewModal,
  MatchOverview,
  MatchResume,
  MatchNotes,
} from './components';

const { FormattedMessage, injectIntl } = Intl;

const REJECTED_STATUS_ID = 0;
const OTHER_STATUSES_LOWER_BOUND = 10;
const SUCCESS_STATUS_LOWER_BOUND = 100;

const SmallTag = styled(Tag)({ width: 8, height: 8 });

const MatchHeader = ({ infoSections }: MatchHeaderProps) => {
  const { id } = useParams();
  const smallTagColor = (() => {
    if (infoSections.matchStatus.id === REJECTED_STATUS_ID) return 'critical';
    if (
      infoSections.matchStatus.id >= OTHER_STATUSES_LOWER_BOUND &&
      infoSections.matchStatus.id < SUCCESS_STATUS_LOWER_BOUND
    )
      return 'pending';
    if (infoSections.matchStatus.id >= SUCCESS_STATUS_LOWER_BOUND)
      return 'success';
    return 'standard';
  })();

  return (
    <Container gutter={4} background="pageSecondary">
      <Breadcrumb>
        <BreadcrumbItem>
          <Flex align="center" gutter={2}>
            <H1>{infoSections.general.candidate?.name}</H1>
            <H2 padding={[0, 0, 3, 0]}>{`(${id})`}</H2>
          </Flex>
        </BreadcrumbItem>
      </Breadcrumb>
      <HeaderInfo>
        <HeaderInfoElement
          label={
            <Tooltip
              overlay={
                <Flex padding={2} align="center" gutter={2}>
                  <Text>
                    <FormattedMessage
                      id={messageIds.vms.matchRecord.matchStatus}
                    />
                  </Text>
                  <Text weight={700}>{infoSections.matchStatus.name}</Text>
                </Flex>
              }
            >
              <Flex gutter={2} align="center">
                <SmallTag color={smallTagColor} circle />
                <span>{infoSections.matchStatus.name}</span>
              </Flex>
            </Tooltip>
          }
        />
        <HeaderInfoElement
          tooltip={
            <FormattedMessage
              id={messageIds.vms.matchRecord.general.positionType}
            />
          }
          label={infoSections.general.position.name}
        />
        <HeaderInfoElement
          tooltip={
            <FormattedMessage id={messageIds.vms.matchRecord.general.company} />
          }
          label={infoSections.general.company?.name}
          icon="company"
        />
        <HeaderInfoElement
          tooltip={
            <FormattedMessage
              id={messageIds.vms.matchRecord.general.hiringManager}
            />
          }
          label={infoSections.general.candidateOwner?.name}
          icon="user"
        />
      </HeaderInfo>
    </Container>
  );
};

const MatchRecord = ({
  fetchMatchInfo,
  isLoadingInfo,
  matchAction,
  infoSections,
  rejectReasons,
}: MatchRecordProps) => {
  const { id } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [offerInterviewModalParams, setOfferInterviewModalParams] = useState<{
    visible: boolean;
    type:
      | Resources.MatchActionType.MakeOffer
      | Resources.MatchActionType.InviteForInterview;
  }>({
    visible: false,
    type: Resources.MatchActionType.InviteForInterview,
  });

  const isInFullPlacement =
    infoSections?.general.statusType ===
    Resources.MatchStatusType.FullPlacement;

  const handleMatchAction = useCallback(
    (matchActionType: Resources.MatchActionType) => {
      if (matchActionType === Resources.MatchActionType.Reject) {
        setRejectModalVisible(true);
      } else {
        setOfferInterviewModalParams({
          visible: true,
          type: matchActionType,
        });
      }
    },
    [],
  );

  const withCloseModal = <T extends {}>(
    func: (values?: T) => void,
  ): ((values?: T) => void) => values => {
    setOfferInterviewModalParams({
      visible: false,
      type: offerInterviewModalParams.type,
    });
    setRejectModalVisible(false);
    func(values);
  };

  useEffect(() => {
    fetchMatchInfo(Number(id));
  }, [fetchMatchInfo, id]);

  if (isLoadingInfo) {
    return <InsightSpinner />;
  }

  if (!infoSections) return null;

  const tabs: TabLayout['tabs'] = [
    {
      key: 'overview',
      name: <FormattedMessage id={messageIds.vms.matchRecord.tabs.overview} />,
      content: <MatchOverview onMatchAction={handleMatchAction} />,
    },
    {
      key: 'candidateDetails',
      name: <FormattedMessage id={messageIds.vms.matchRecord.tabs.resume} />,
      content: <MatchResume />,
      permission: AppPermissions.ViewMatchCandidateDetails,
    },
    {
      key: 'credentials',
      name: (
        <FormattedMessage id={messageIds.vms.matchRecord.tabs.credentials} />
      ),
      content: <Credentials />,
      permission: `${AppPermissions.ViewMatchCredentials} & (${isInFullPlacement} | ${AppPermissions.ViewMatchCredentialsBeforeFullPlacement})`,
    },
    {
      key: 'notes',
      name: <FormattedMessage id={messageIds.vms.matchRecord.tabs.notes} />,
      content: <MatchNotes />,
      permission: AppPermissions.ViewMatchNotes,
    },
  ];

  return (
    <Container
      padding={[4, 0, 0, 0]}
      gutter={4}
      background="pageSecondary"
      stretch
      ref={containerRef}
    >
      <Flex direction="column" gutter={2} gutterDirection="vertical">
        <FlexItem padding={[0, 12, 10, 12]}>
          <MatchHeader infoSections={infoSections} />
        </FlexItem>
      </Flex>
      <OfferInterviewModal
        onCancel={() =>
          setOfferInterviewModalParams({
            ...offerInterviewModalParams,
            visible: false,
          })
        }
        visible={offerInterviewModalParams.visible}
        type={offerInterviewModalParams.type}
        onConfirm={withCloseModal(() =>
          matchAction({
            matchActionType: offerInterviewModalParams.type,
            matchId: infoSections.general.id,
            matchCandidateName: infoSections.general.candidate.name,
          }),
        )}
      />
      <RejectModal
        onCancel={() => setRejectModalVisible(false)}
        visible={rejectModalVisible}
        onSubmit={withCloseModal(values =>
          matchAction({
            matchActionType: Resources.MatchActionType.Reject,
            matchId: infoSections.general.id,
            note: values?.additionalNote,
            reasonId: Number(values?.rejectReason),
            matchCandidateName: infoSections.general.candidate.name,
          }),
        )}
        rejectReasons={rejectReasons}
      />
      <TabLayout tabs={tabs} routable stickTo={containerRef} />
    </Container>
  );
};

export default injectIntl(
  connect(
    (state: RootState) => ({
      isLoadingInfo: selectors.selectMatchRecord(state).isLoadingInfo,
      infoSections: selectors.selectMatchRecord(state).infoSections,
      rejectReasons: selectors.selectMatchRecord(state).rejectReasons,
    }),
    { fetchMatchInfo, matchAction },
  )(MatchRecord),
);
